import {
  fetchApps,
  fetchLlmKeys,
  selectAppsByOrgAsList,
  selectDatabasesByOrgAsList,
  selectEndpointsByOrgAsList,
  selectEnvironmentsByOrgAsList,
  selectHasDedicatedStack,
  selectHasSelfHostedStack,
  selectLlmKeysByOrgAsList,
} from "@app/deploy";
import { useLoader, useSelector } from "@app/react";
import { createStackUrl, deployUrl, llmGatewayUrl } from "@app/routes";
import {
  Banner,
  IconAI,
  IconArrowRight,
  IconCheck,
  tokens,
} from "@app/ui/shared";
import { useMemo } from "react";
import { Link } from "react-router-dom";

const STEP_COLOR = "#283a99";
const STEP_COLOR_LIGHT = "#ffffff";

const StepCircleFilled = ({ children }: { children: React.ReactNode }) => (
  <span
    className="flex-shrink-0 w-7 h-7 rounded-full text-white text-sm font-medium flex items-center justify-center"
    style={{ backgroundColor: STEP_COLOR }}
  >
    {children}
  </span>
);

const StepCircleOutlined = ({ children }: { children: React.ReactNode }) => (
  <span
    className="flex-shrink-0 w-7 h-7 rounded-full text-sm font-medium flex items-center justify-center border-2"
    style={{ borderColor: STEP_COLOR, color: STEP_COLOR }}
  >
    {children}
  </span>
);

interface OnboardingStep {
  title: string;
  description: string;
  actionUrl: string;
  docsUrl: string;
  isComplete: boolean;
}

interface OnboardingStatus {
  hasEnvironments: boolean;
  hasApps: boolean;
  hasDatabases: boolean;
  hasEndpoints: boolean;
  hasDedicatedOrSelfHostedStack: boolean;
}

const STEPS_CONFIG = [
  {
    title: "Create your first environment",
    description: "Group resources together (e.g. staging, dev, production).",
    actionUrl: deployUrl(),
    docsUrl:
      "https://www.aptible.com/docs/core-concepts/architecture/environments",
    statusKey: "hasEnvironments" as const,
  },
  {
    title: "Deploy your code or a starter template",
    description: "Deploy an app using Git push or Docker image.",
    actionUrl: deployUrl(),
    docsUrl: "https://www.aptible.com/docs/core-concepts/apps/overview",
    statusKey: "hasApps" as const,
  },
  {
    title: "Provision a database",
    description: "Add a managed database for persistent storage.",
    actionUrl: deployUrl(),
    docsUrl:
      "https://www.aptible.com/docs/core-concepts/managed-databases/overview",
    statusKey: "hasDatabases" as const,
  },
  {
    title: "Expose your app to the internet",
    description: "Create an endpoint to make your app accessible.",
    actionUrl: deployUrl(),
    docsUrl:
      "https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/overview#app-endpoints",
    statusKey: "hasEndpoints" as const,
  },
  {
    title: "Setup a production stack",
    description:
      "Provision a Dedicated Stack for production, with BAA access and added security/compliance support.",
    actionUrl: createStackUrl(),
    docsUrl: "https://www.aptible.com/docs/core-concepts/architecture/stacks",
    statusKey: "hasDedicatedOrSelfHostedStack" as const,
  },
];

export const SetupWidget = () => {
  const environments = useSelector(selectEnvironmentsByOrgAsList);
  const apps = useSelector(selectAppsByOrgAsList);
  const databases = useSelector(selectDatabasesByOrgAsList);
  const endpoints = useSelector(selectEndpointsByOrgAsList);
  const hasDedicatedStack = useSelector(selectHasDedicatedStack);
  const hasSelfHostedStack = useSelector(selectHasSelfHostedStack);
  const appsLoader = useLoader(fetchApps);

  const llmKeysLoader = useLoader(fetchLlmKeys);
  const llmKeys = useSelector(selectLlmKeysByOrgAsList);

  const status: OnboardingStatus = useMemo(
    () => ({
      hasEnvironments: environments.length > 0,
      hasApps: apps.length > 0,
      hasDatabases: databases.length > 0,
      hasEndpoints: endpoints.length > 0,
      hasDedicatedOrSelfHostedStack: hasDedicatedStack || hasSelfHostedStack,
    }),
    [environments, apps, databases, endpoints, hasDedicatedStack],
  );

  const steps: OnboardingStep[] = useMemo(
    () =>
      STEPS_CONFIG.map((step) => ({
        title: step.title,
        description: step.description,
        actionUrl: step.actionUrl,
        docsUrl: step.docsUrl,
        isComplete: status[step.statusKey],
      })),
    [status],
  );

  if (!appsLoader.isSuccess || steps.every((step) => step.isComplete)) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow">
      <div className="p-6 pb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Getting Started
        </h2>
      </div>
      <ol className="list-none p-0 m-0 divide-y divide-gray-200 px-6 pb-6">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className={
              step.isComplete ? "bg-gray-50 py-4" : "py-4 first:pt-0 last:pb-0"
            }
          >
            <div className="flex items-start gap-4">
              {step.isComplete ? (
                <>
                  <div className="flex-shrink-0 w-14 flex justify-center pt-0.5">
                    <StepCircleFilled>
                      <IconCheck className="w-4 h-4" color={STEP_COLOR_LIGHT} />
                    </StepCircleFilled>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-500">
                      {step.title}
                    </div>
                    <div className="text-sm text-gray-500">
                      {step.description}{" "}
                      <a
                        href={step.docsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-400 hover:text-blue-600"
                      >
                        Learn more.
                      </a>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex w-full min-w-0 items-start gap-4">
                  <Link
                    to={step.actionUrl}
                    className="flex flex-shrink-0 flex-col items-center no-underline hover:no-underline"
                  >
                    <div className="w-14 flex justify-center pt-0.5">
                      <StepCircleOutlined>{index + 1}</StepCircleOutlined>
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={step.actionUrl}
                      className="font-medium text-gray-900 no-underline hover:no-underline"
                    >
                      {step.title}
                    </Link>
                    <div className="text-sm text-gray-500">
                      {step.description}{" "}
                      <a
                        href={step.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-600"
                      >
                        Learn more.
                      </a>
                    </div>
                  </div>
                  <Link
                    to={step.actionUrl}
                    className="flex flex-shrink-0 items-center pt-0.5 ml-auto no-underline hover:no-underline"
                  >
                    <IconArrowRight className="w-5 h-5 text-gray-400" />
                  </Link>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
      {!llmKeysLoader.isLoading && llmKeys.length === 0 && (
        <div className="px-6 pb-6">
          <Banner variant="progress" showIcon={false}>
            <div className="flex items-center gap-2">
              <IconAI variant="base" />
              <p className={`${tokens.type.darker} mt-0.5`}>
                <strong>Prefer to start with the LLM Gateway?</strong>{" "}
                <Link to={llmGatewayUrl()} className="underline">
                  Create your first LLM key →
                </Link>
              </p>
            </div>
          </Banner>
        </div>
      )}
    </div>
  );
};
